/**
 * 共有フォルダのタブの判定(#267)。
 *
 * **DOM を触らない純粋な関数だけを置く**(ビューアのテストは `node` 環境 ——
 * [ADR-0019](../../../../docs/adr/0019-dev-foundation.md))。ページの組み立ては `folder.ts`。
 */

/** URL の末尾に残すタブの番号のキー(`#tab=2`)。 */
const TAB_PARAM = 'tab';

/** 番号の形。**1 始まりの整数だけ**(`0`・`02`・`1.5`・`-1` は受けない)。 */
const TAB_NUMBER = /^[1-9]\d*$/u;

/**
 * URL の `#tab=N` から、開くタブの位置(**0 始まり**)を決める。
 *
 * **番号は 1 始まり**(人が読み書きする URL なので)。**範囲外・不正な値・タブが無いときは先頭。**
 *
 * **番号は並びの位置**で、盤面を外すとずれる(人が読み書きする URL なので位置にした ——
 * #267 の同意書 Q3)。タブの `id` は公開 API が返すが、**閲覧の通知のためだけ**に使う
 * ([ADR-0051](../../../../docs/adr/0051-folder-view-count.md) の決定 4)。
 */
export function tabFromHash(hash: string, count: number): number {
  if (count <= 0) return 0;
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const raw = params.get(TAB_PARAM);
  if (raw === null || !TAB_NUMBER.test(raw)) return 0;
  const n = Number(raw);
  return n <= count ? n - 1 : 0;
}

/** タブの位置(0 始まり)を、URL の末尾の形(`#tab=N`)にする。 */
export function hashForTab(index: number): string {
  return `#${TAB_PARAM}=${String(index + 1)}`;
}

/**
 * キー操作で移る先のタブ([WAI-ARIA のタブの作法](https://www.w3.org/WAI/ARIA/apg/patterns/tabs/))。
 * **移らないキーは `null`**(呼び出し側は既定の動きを止めない)。
 *
 * - 左右 —— 隣へ。**端では反対の端へ回り込む**
 * - Home / End —— 先頭 / 末尾
 */
export function nextTab(current: number, key: string, count: number): number | null {
  if (count <= 0) return null;
  switch (key) {
    case 'ArrowRight':
      return (current + 1) % count;
    case 'ArrowLeft':
      return (current - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

/**
 * 閲覧の通知に使うタブの `id`(#290 / [ADR-0051](../../../../docs/adr/0051-folder-view-count.md) の
 * 決定 7)。**無ければ `null`**(送らない)。
 *
 * 公開 API は最大 60 秒キャッシュされるので、配備の直後は**タブに `id` が無い古い応答**が返りうる。
 * 型の上では必ずあるが、**実際の応答を信じない**(文字列でない・空も送らない)。
 */
export function tabViewId(tab: { readonly id?: unknown }): string | null {
  return typeof tab.id === 'string' && tab.id !== '' ? tab.id : null;
}

/**
 * シェルの知らせを受けて、**閲覧を通知するタブの `id`**(#290 / ADR-0051 の決定 7)。**送らないなら `null`**。
 *
 * - **表示が終わった(`ready`)ときだけ**(盤面の単体ページと同じ「表示できたときだけ」)
 * - **いまのシェルの知らせだけ**(捨てたシェルが後から `ready` を出しても送らない ——
 *   表示が終わる前に別のタブへ移った盤面は数えない)
 * - タブに `id` が無ければ送らない(`tabViewId`)
 */
export function folderViewTarget(
  eventType: string,
  isCurrentShell: boolean,
  tab: { readonly id?: unknown },
): string | null {
  if (eventType !== 'ready' || !isCurrentShell) return null;
  return tabViewId(tab);
}
